import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import { api } from "@/lib/api";
import { useState } from "react";
import { toast } from "sonner";
import { Instagram } from "lucide-react";
import { QualitaetLogo, LajenLogo, SuedtirolLogo } from "@/components/QualitaetLogo";

const LEGAL_LABELS = {
  de: { impressum: "Impressum", datenschutz: "Datenschutz", agb: "AGB", widerruf: "Widerruf", giftcard: "Gutschein" },
  en: { impressum: "Legal Notice", datenschutz: "Privacy", agb: "Terms", widerruf: "Withdrawal", giftcard: "Gift Card" },
  it: { impressum: "Note Legali", datenschutz: "Privacy", agb: "Termini", widerruf: "Recesso", giftcard: "Buono Regalo" },
  ja: { impressum: "法的表示", datenschutz: "プライバシー", agb: "利用規約", widerruf: "撤回権", giftcard: "ギフトカード" },
  zh: { impressum: "法律信息", datenschutz: "隐私政策", agb: "使用条款", widerruf: "撤销权", giftcard: "礼品卡" },
};

const REVIEW_LINK = {
  de: "Bewertung abgeben",
  en: "Leave a review",
  it: "Lascia una recensione",
  ja: "レビューを書く",
  zh: "撰写评价",
};

export default function Footer() {
  const { t, lang } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/newsletter", { email, language: lang });
      toast.success(t.newsletter.ok);
      setEmail("");
    } catch { toast.error("Error"); } finally { setBusy(false); }
  };

  return (
    <footer className="relative bg-black border-t border-white/5 mt-32">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-4 gap-16 mb-20">
          <div>
            <img
              src="/logo-v2.png?v=2"
              alt="Madubau Dolomiti"
              className="w-56 h-auto -ml-2 mb-6"
              data-testid="footer-logo"
            />
          </div>

          <div>
            <h4 className="font-display text-[10px] tracking-[0.3em] text-gold mb-6">Explore</h4>
            <ul className="space-y-3 text-sm text-cream/70 font-body">
              <li><Link to="/shop" className="hover:text-gold transition-colors">{t.nav.shop}</Link></li>
              <li><Link to="/story" className="hover:text-gold transition-colors">{t.nav.story}</Link></li>
              <li><Link to="/gallery" className="hover:text-gold transition-colors">{t.nav.gallery}</Link></li>
              <li><Link to="/contact" className="hover:text-gold transition-colors">{t.nav.contact}</Link></li>
              <li>
                <Link to="/bewertung" data-testid="footer-review-link" className="inline-flex items-center gap-2 hover:text-gold transition-colors text-gold/80">
                  ★ {REVIEW_LINK[lang] || REVIEW_LINK.de}
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-[10px] tracking-[0.3em] text-gold mb-6">Contact</h4>
            <ul className="space-y-3 text-sm text-cream/70 font-body">
              <li>Gorneggweg 4A</li>
              <li>39040 Laion, Italy</li>
              <li><a href="mailto:info@madubau.com" className="hover:text-gold">info@madubau.com</a></li>
              <li>
                <a
                  href="https://wa.me/393793407070"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="whatsapp-link"
                  className="inline-flex items-center gap-2 hover:text-gold transition-colors"
                >
                  <svg viewBox="0 0 32 32" className="w-4 h-4" fill="#25D366" aria-hidden="true">
                    <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39a.63.63 0 0 1-.315-.1c-.802-.402-1.504-.817-2.163-1.447-.545-.516-1.146-1.29-1.46-1.963a.426.426 0 0 1-.073-.215c0-.33.99-.945.99-1.49 0-.143-.73-2.09-.832-2.335-.143-.372-.214-.487-.6-.487-.187 0-.36-.043-.53-.043-.302 0-.53.115-.746.315-.688.645-1.032 1.318-1.06 2.264v.114c-.015.99.472 1.977 1.017 2.78 1.23 1.82 2.506 3.41 4.554 4.34.616.287 2.035.888 2.722.888.817 0 2.15-.515 2.478-1.318.13-.33.245-.688.245-1.06 0-.058 0-.144-.014-.216-.101-.36-.144-.372-.816-.688-.616-.286-1.202-.5-1.876-.688zM16.147 4.5c-6.352 0-11.5 5.148-11.5 11.5 0 2.078.556 4.02 1.53 5.702L4.5 27.5l5.926-1.548A11.474 11.474 0 0 0 16.147 27.5c6.352 0 11.5-5.148 11.5-11.5s-5.148-11.5-11.5-11.5zm0 21.005c-1.75 0-3.4-.487-4.803-1.318l-.344-.202-3.567.938.958-3.481-.229-.36a9.482 9.482 0 0 1-1.462-5.082c0-5.248 4.267-9.515 9.515-9.515s9.515 4.267 9.515 9.515-4.269 9.505-9.583 9.505z"/>
                  </svg>
                  +39 379 340 7070
                </a>
              </li>
              <li>
                <a
                  href="https://www.instagram.com/madubau_honey/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="instagram-link"
                  className="inline-flex items-center gap-2 hover:text-gold transition-colors"
                >
                  <Instagram className="w-4 h-4" /> @madubau_honey
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-display text-[10px] tracking-[0.3em] text-gold mb-6">{t.newsletter.eyebrow}</h4>
            <p className="text-sm text-cream/60 font-body mb-4">{t.newsletter.sub}</p>
            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.newsletter.ph}
                data-testid="footer-newsletter-email"
                className="bg-transparent border-b border-white/20 focus:border-gold text-sm text-cream py-3 outline-none placeholder:text-cream/40 transition-colors"
              />
              <button
                type="submit"
                disabled={busy}
                data-testid="footer-newsletter-submit"
                className="font-display text-[10px] tracking-[0.3em] text-gold hover:text-gold-light self-start link-underline"
              >
                → {t.newsletter.cta}
              </button>
            </form>
          </div>
        </div>

        <div className="flex flex-wrap justify-center items-center gap-8 pb-8" data-testid="footer-quality-seal">
          <QualitaetLogo size="lg" testid="footer-qualitaet-logo" />
          <LajenLogo size="lg" testid="footer-lajen-logo" />
          <SuedtirolLogo size="lg" testid="footer-suedtirol-logo" />
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-xs text-cream/40 font-body">© {new Date().getFullYear()} Madubau Dolomiti · {t.footer.rights}</p>
          <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-cream/50 font-body" data-testid="footer-legal">
            <li><Link to="/impressum" className="hover:text-gold" data-testid="footer-impressum">{(LEGAL_LABELS[lang] || LEGAL_LABELS.en).impressum}</Link></li>
            <li><Link to="/datenschutz" className="hover:text-gold" data-testid="footer-datenschutz">{(LEGAL_LABELS[lang] || LEGAL_LABELS.en).datenschutz}</Link></li>
            <li><Link to="/agb" className="hover:text-gold" data-testid="footer-agb">{(LEGAL_LABELS[lang] || LEGAL_LABELS.en).agb}</Link></li>
            <li><Link to="/widerruf" className="hover:text-gold" data-testid="footer-widerruf">{(LEGAL_LABELS[lang] || LEGAL_LABELS.en).widerruf}</Link></li>
            <li><Link to="/gift-card" className="text-gold hover:text-gold-light" data-testid="footer-giftcard">🎁 {(LEGAL_LABELS[lang] || LEGAL_LABELS.en).giftcard}</Link></li>
          </ul>
        </div>
      </div>
    </footer>
  );
}
